<template>
	<div class="pa-2">
		<!-- dialog -->
		<v-dialog v-model="dialog" max-width="500">
			<v-form ref="form" :disabled="submitLoading" @submit.prevent="submit">
				<v-card>
					<v-card-title>
						<span>{{ `${!itemId ? $T('add') : $T('edit')} ${$T('reminder')}` }}</span>
						<v-spacer />
						<v-btn icon @click="dialog = false">
							<v-icon>$close</v-icon>
						</v-btn>
					</v-card-title>
					<v-card-text>
						<v-row dense>
							<!-- employee -->
							<v-col sm="6" cols="12">
								<v-autocomplete
									v-model="dialogData.employeeId"
									:label="$T('inputs.employee-name')"
									:items="employees"
									:item-text="item => `(${item.code}) ${item.arabicFullName}`"
									item-value="employeeId"
									dense
									outlined
									hide-details
									:rules="[rules.required]"
									@change="dialogData.sectionId = getEmployeeById($event).sectionId"
								></v-autocomplete>
							</v-col>

							<!-- section name -->
							<v-col sm="6" cols="12">
								<v-autocomplete
									v-model="dialogData.sectionId"
									:label="$T('inputs.section-name')"
									:items="sections"
									item-text="name"
									item-value="id"
									dense
									outlined
									hide-details
									:rules="[rules.required]"
								></v-autocomplete>
							</v-col>

							<!-- reminder type -->
							<v-col sm="6" cols="12">
								<v-autocomplete
									v-model="dialogData.reminderTypeId"
									:label="$T('inputs.reminder-type')"
									:items="remindersTypes"
									item-text="name"
									item-value="id"
									dense
									outlined
									hide-details
									:rules="[rules.required]"
								></v-autocomplete>
							</v-col>

							<!-- date -->
							<v-col sm="6" cols="12">
								<bee-date-picker
									v-model="dialogData.date"
									:input-label="$T('inputs.reminderDate')"
									:input-props="{
										readonly: true,
										outlined: true,
										dense: true,
										prependInnerIcon: '$calendar'
									}"
									:years-over-max="2"
									outlined
									menu-picker
									dense
									hide-details
									:rules="[rules.required]"
								></bee-date-picker>
							</v-col>

							<!-- notes -->
							<v-col cols="12">
								<v-textarea
									v-model="dialogData.notes"
									:label="$T('inputs.notes')"
									rows="1"
									auto-grow
									dense
									outlined
									hide-details
								/>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<v-spacer />
						<v-btn color="secondary" :loading="submitLoading" type="submit">
							{{ $T('default-buttons.save') }}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<!-- delete dialog -->
		<v-delete-dialog
			v-model="deleteDialog"
			module="reminders/delete"
			:parameters="{ id: itemId }"
			@on-delete="filter()"
		>
			{{ $T('Reminders.delete-message-reminder') }}
		</v-delete-dialog>

		<!-- filter -->
		<v-form ref="filterForm" @submit.prevent="filter()">
			<v-row class="mt-0 mb-2" align="center" dense>
				<!-- employee -->
				<v-col cols="6" sm="4" md="2">
					<v-autocomplete
						v-model="filterData.employeeId"
						:items="employees"
						:item-text="item => `(${item.code}) ${item.arabicFullName}`"
						item-value="employeeId"
						dense
						outlined
						:label="$T('inputs.employee-name')"
						hide-details
					></v-autocomplete>
				</v-col>
			
				<!-- section -->
				<v-col cols="6" sm="4" md="2">
					<v-autocomplete
						v-model="filterData.sectionId"
						:items="sections"
						item-text="name"
						item-value="id"
						dense
						outlined
						:label="$T('inputs.search-section')"
						hide-details
					></v-autocomplete>
				</v-col>
				
				<!-- reminder type -->
				<v-col cols="6" sm="4" md="2">
					<v-autocomplete
						v-model="filterData.reminderTypeId"
						:items="remindersTypes"
						item-text="name"
						item-value="id"
						dense
						outlined
						:label="$T('inputs.reminder-type')"
						hide-details
					></v-autocomplete>
				</v-col>
				
				<!-- date -->
				<v-col cols="6" sm="4" md="2">
					<bee-date-picker
						v-model="filterData.date"
						:input-label="$T('inputs.date')"
						:input-props="{ dense: true, outlined: true, readonly: true, prependInnerIcon: '$calendar' }"
						menu-picker
						:years-over-max="2"
						:items="[]"
						dense
						outlined
						hide-details
					></bee-date-picker>
				</v-col>
				
				<!-- actions -->
				<v-col cols="auto">
					<v-btn
						outlined
						small
						color="primary"
						class="me-1"
						type="submit"
					>
						<v-icon small left>mdi-magnify</v-icon>
						{{ $T('default-buttons.search') }}
					</v-btn>
					<v-btn
						style="min-width: 30px"
						class="px-0"
						outlined
						small
						color="alert"
						@click="$refs.filterForm.reset(); filter();"
					>
						<v-icon small> $clear </v-icon>
					</v-btn>
				</v-col>
			
				<v-spacer></v-spacer>
				<v-divider vertical class="mx-1"></v-divider>
				<v-col cols="auto">
					<v-btn
						color="primary"
						small
						@click="dialog = true"
					>
						<v-icon size="20" left>$add</v-icon>
						{{ $T('add') }}
					</v-btn>
				</v-col>
				
				<v-col v-if="false" cols="12">
					<v-radio-group v-model="searchType" mandatory class="mt-0" dense row hide-details>
						<v-radio :label="$T('inputs.reminderType.salaryIncrease')" value="0" />
						<v-radio :label="$T('inputs.reminderType.admission')" value="1" />
						<v-radio :label="$T('inputs.reminderType.all')" value="2" />
					</v-radio-group>
				</v-col>
			</v-row>
		</v-form>

		<!-- table -->
		<bee-handy-table
			:headers="tableHeaders"
			:items="!loading ? reminders : []"
			:loading="loading"
			pagination-on-scroll
			:height="$vuetify.breakpoint.height - 100"
			fixed-header
			:shown-rows-text="$T('shown-rows-text')"
			:data-table-props="{ disableSort: true }"
			dense
			zebra
		>
			<template v-slot:item.actions="{ item }">
				<tooltip :text="$T('tooltips.preview')">
					<v-btn small icon @click="itemId = item.id; dialog = true;">
						<v-icon size="20" color="edit-color">$edit</v-icon>
					</v-btn>
				</tooltip>
				<tooltip :text="$T('tooltips.delete')">
					<v-btn small icon @click="itemId = item.id; deleteDialog = true;">
						<v-icon size="20" color="alert">$delete</v-icon>
					</v-btn>
				</tooltip>
			</template>
		</bee-handy-table>
	</div>
</template>

<script>
import { DATE_FORMAT } from '@/utils/moment';
import { mapGetters, mapState } from 'vuex';
import moment from 'moment';
import rules from '@/utils/validation rules';

export default {
	name: 'Reminders',

	data() {
		return {
			submitLoading: false,
			loading: false,

			rules,

			filterData: {
				employeeId: null,
				sectionId: null,
				reminderTypeId: null,
				date: new Date().toISOString().slice(0, 10),
			},
			searchType: null,

			deleteDialog: false,

			itemId: null,
			dialog: false,
			dialogData: {
				employeeId: null,
				sectionId: null,
				reminderTypeId: null,
				date: null,
				notes: null,
			},

			tableHeaders: [
				{
					text: this.$T('headers.employee-name'),
					name: 'employeeId',
					value: 'employeeId',
					setValueExpr: val => this.getEmployeeById(val)?.arabicFullName ?? '-'
				},
				{
					text: this.$T('headers.section-name'),
					name: 'sectionId',
					value: 'sectionId',
					setValueExpr: val => this.getSectionById(val)?.name ?? '-'
				},
				{
					text: this.$T('headers.reminderName'),
					name: 'reminderTypeId',
					value: 'reminderTypeId',
					setValueExpr: val => this.getReminderTypeById(val)?.name ?? '-'
				},
				{
					text: this.$T('headers.reminderDate'),
					name: 'date',
					value: 'date',
					type: 'date',
					dateFormat: DATE_FORMAT,
				},
				{
					text: this.$T('headers.notes'),
					name: 'notes',
					value: 'notes',
					width: 350
				},
				{ text: '', name: 'actions', value: 'actions', width: 70, columnClass: 'px-1' },
			],
		};
	},

	watch: {
		dialog(val) {
			if (val) {
				if (this.itemId) {
					const reminder = this.getReminderById(this.itemId);
					this.dialogData.employeeId = reminder.employeeId;
					this.dialogData.sectionId = reminder.sectionId;
					this.dialogData.reminderTypeId = reminder.reminderTypeId;
					this.dialogData.date = moment(reminder.date).format('YYYY-MM-DD');
					this.dialogData.notes = reminder.notes;
				}
			} else {
				this.$refs.form.reset();
				this.itemId = null;
			}
		},

		deleteDialog(val) {
			if (!val) this.itemId = null;
		}
	},

	computed: {
		...mapState({
			employees: state => state.employee.employees,
			sections: state => state.section.sections,
			reminders: state => state.reminders.reminders,
			remindersTypes: state => state.remindersTypes.remindersTypes,
		}),

		...mapGetters({
			getSectionById: 'section/getSectionById',
			getEmployeeById: 'employee/getEmployeeById',
			getReminderById: 'reminders/getReminderById',
			getReminderTypeById: 'remindersTypes/getReminderTypeById',
		})
	},

	methods: {
		filter(isFirstLoad) {
			this.loading = true;
			this.$store.dispatch('reminders/fetchAll', {
				employeeId: this.filterData.employeeId,
				sectionId: this.filterData.sectionId,
				reminderTypeId: this.filterData.reminderTypeId,
				date: this.filterData.date,
			}).finally(() => {
				if (!isFirstLoad) this.loading = false;
			})
		},

		submit() {
			if (this.$refs.form.validate()) {
				this.submitLoading = true;
				const actionName = !this.itemId ? 'create' : 'update';
				this.$store.dispatch(`reminders/${actionName}`, {
					id: this.itemId,
					employeeId: this.dialogData.employeeId,
					sectionId: this.dialogData.sectionId,
					reminderTypeId: this.dialogData.reminderTypeId,
					date: this.dialogData.date,
					notes: this.dialogData.notes
				}).then(() => {
					this.filter();
					this.$eventBus.$emit(
						'show-snackbar',
						!this.itemId ? this.$T('messages.add-done') : this.$T('messages.edit-done'),
						{ done: true }
					);
					this.dialog = false;
				}).catch((err) => {
					err.default && err.default(
						!this.itemId ? this.$T('messages.add-fail') : this.$T('messages.edit-fail'),
						false
					);
				}).finally(() => {
					this.submitLoading = false;
				})
			}
		},
	},

	created() {
		this.loading = true;
		Promise.all([
			this.filter(true),
			this.remindersTypes.length === 0 ? this.$store.dispatch('remindersTypes/fetchAll') : null,
		]).finally(() => {
			this.loading = false;
		})
	}
};
</script>

<style lang="scss" scoped></style>
